// Pure helpers backing the custom-integration endpoint editor in
// `integrations.tsx`. Lives in its own module (no React imports) so the
// row ↔ JSON-Schema round-trip can be exercised by the Node-based test
// runner without dragging in JSX or browser-only deps.
//
// The shapes here mirror what the server expects on the wire:
//   - `parameters` is a (loosely-validated) JSON-Schema object.
//   - `paramIn` is an optional sibling map that pins where each named
//     parameter goes on the request (path / query / body / header).
// "auto" placement is a UI-only convenience that means "let the server
// pick a default" — it is never persisted, which is why we strip it in
// `buildParamsFromRows`.

export type ParamType = "string" | "number" | "integer" | "boolean";
export type ParamPlacement = "auto" | "path" | "query" | "body" | "header";

export interface ParamRow {
  name: string;
  type: ParamType;
  description: string;
  required: boolean;
  // "auto" means: leave it to the server's default placement (path if
  // referenced in the URL template, query for GET/DELETE, body otherwise).
  placement: ParamPlacement;
  // Comma-separated enum values the LLM is constrained to pick from
  // (e.g. "open, in_progress, closed"). Empty / omitted → no enum
  // constraint. Stored as the raw text so the admin's spacing / order
  // survives re-renders; we split + sanitise on save. Optional so older
  // call sites (and the existing test fixtures) that pre-date the enum
  // feature continue to compile.
  enumCsv?: string;
  // Optional numeric bounds. Only meaningful when type is number/integer
  // — the UI hides them otherwise and the server drops them on save for
  // non-numeric params. Strings (not numbers) so the input stays
  // controlled and "" can mean "no bound".
  minimum?: string;
  maximum?: string;
}

// Concrete (non-"auto") placements actually persisted to the server.
type PersistedPlacement = Exclude<ParamPlacement, "auto">;

// JSON-Schema property the editor knows how to round-trip. `enum`,
// `minimum`, `maximum` are optional and only emitted when meaningful.
export interface BuiltProperty {
  type?: string;
  description?: string;
  enum?: unknown[];
  minimum?: number;
  maximum?: number;
}

export interface BuiltParams {
  parameters: {
    type: "object";
    properties: Record<string, BuiltProperty>;
    required?: string[];
  };
  paramIn?: Record<string, PersistedPlacement>;
}

// JSON-Schema property → row. Tolerant of unknown / missing types so we
// don't silently drop existing definitions; defaults to "string" which
// is the permissive default the server uses too.
export function propertyToRow(
  name: string,
  pdef: BuiltProperty | undefined,
  required: Set<string>,
  paramIn: Record<string, ParamPlacement> | undefined,
): ParamRow {
  const declared = pdef?.type;
  const type: ParamType =
    declared === "number" || declared === "integer" || declared === "boolean" || declared === "string"
      ? declared
      : "string";
  const placement = paramIn?.[name];
  // Stringify enum members for the chip/CSV input. Booleans flatten to
  // "true" / "false"; nested objects (which validateEndpoint would have
  // dropped anyway) get JSON-encoded so the admin can at least see them.
  const enumCsv = Array.isArray(pdef?.enum)
    ? pdef!.enum
        .map((v) =>
          typeof v === "string"
            ? v
            : typeof v === "number" || typeof v === "boolean"
              ? String(v)
              : JSON.stringify(v),
        )
        .join(", ")
    : "";
  return {
    name,
    type,
    description: pdef?.description ?? "",
    required: required.has(name),
    placement:
      placement === "path" || placement === "query" || placement === "body" || placement === "header"
        ? placement
        : "auto",
    // Only include enum / min / max keys when they actually carry a
    // value. Empty strings would round-trip back to the same parsed
    // schema, but they'd also pollute deep-equal comparisons in test
    // fixtures and editor state for connectors that don't use these
    // fields.
    ...(enumCsv ? { enumCsv } : {}),
    ...(typeof pdef?.minimum === "number" && Number.isFinite(pdef.minimum)
      ? { minimum: String(pdef.minimum) }
      : {}),
    ...(typeof pdef?.maximum === "number" && Number.isFinite(pdef.maximum)
      ? { maximum: String(pdef.maximum) }
      : {}),
  };
}

// Build the JSON-Schema fragment + paramIn map the server expects from
// the row state. Empty/unnamed rows are dropped silently — they're just
// blank scaffolding the admin hasn't filled in.
export function buildParamsFromRows(rows: ParamRow[]): BuiltParams {
  const properties: Record<string, BuiltProperty> = {};
  const required: string[] = [];
  const paramIn: Record<string, PersistedPlacement> = {};
  for (const p of rows) {
    const name = p.name.trim();
    if (!name) continue;
    // Split the CSV chip text into individual values, then coerce per
    // declared type. We keep the same behaviour the server enforces so
    // the JSON preview / round-trip stays stable: blanks dropped,
    // numerics parsed for number/integer params, booleans only accept
    // "true"/"false". Empty arrays are omitted so the JSON view doesn't
    // sprout `"enum": []` for params that don't use the constraint.
    const enumCsv = p.enumCsv ?? "";
    const enumPieces = enumCsv
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    let enumValues: unknown[] | undefined;
    if (enumPieces.length) {
      const out: unknown[] = [];
      const seen = new Set<string>();
      for (const piece of enumPieces) {
        let coerced: unknown | undefined;
        if (p.type === "number" || p.type === "integer") {
          const n = Number(piece);
          if (Number.isFinite(n) && (p.type === "number" || Number.isInteger(n))) coerced = n;
        } else if (p.type === "boolean") {
          if (piece === "true") coerced = true;
          else if (piece === "false") coerced = false;
        } else {
          coerced = piece;
        }
        if (coerced === undefined) continue;
        const key = typeof coerced === "string" ? `s:${coerced}` : `j:${JSON.stringify(coerced)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(coerced);
      }
      if (out.length) enumValues = out;
    }
    let minimum: number | undefined;
    let maximum: number | undefined;
    if (p.type === "number" || p.type === "integer") {
      const minStr = (p.minimum ?? "").trim();
      const maxStr = (p.maximum ?? "").trim();
      if (minStr !== "") {
        const n = Number(minStr);
        if (Number.isFinite(n) && (p.type === "number" || Number.isInteger(n))) minimum = n;
      }
      if (maxStr !== "") {
        const n = Number(maxStr);
        if (Number.isFinite(n) && (p.type === "number" || Number.isInteger(n))) maximum = n;
      }
    }
    properties[name] = {
      type: p.type,
      ...(p.description.trim() ? { description: p.description.trim() } : {}),
      ...(enumValues ? { enum: enumValues } : {}),
      ...(minimum !== undefined ? { minimum } : {}),
      ...(maximum !== undefined ? { maximum } : {}),
    };
    if (p.required) required.push(name);
    if (p.placement !== "auto") paramIn[name] = p.placement;
  }
  return {
    parameters: {
      type: "object",
      properties,
      ...(required.length ? { required } : {}),
    },
    paramIn: Object.keys(paramIn).length ? paramIn : undefined,
  };
}

// Inverse of buildParamsFromRows — used when the admin toggles back from
// the JSON view so their hand-edited JSON syncs into the row form.
export function rowsFromParsedSchema(parsed: any): ParamRow[] {
  if (!parsed || typeof parsed !== "object") return [];
  const properties =
    parsed.properties && typeof parsed.properties === "object" ? parsed.properties : {};
  const required = new Set<string>(
    Array.isArray(parsed.required)
      ? parsed.required.filter((r: unknown) => typeof r === "string")
      : [],
  );
  const paramIn =
    parsed.paramIn && typeof parsed.paramIn === "object" ? parsed.paramIn : undefined;
  return Object.entries<any>(properties).map(([name, pdef]) =>
    propertyToRow(name, pdef, required, paramIn),
  );
}

// We splice paramIn into the same JSON object so power users see one
// unified document — the save path strips it back out before posting.
export function previewJson(rows: ParamRow[]): string {
  const { parameters, paramIn } = buildParamsFromRows(rows);
  const merged: any = { ...parameters };
  if (paramIn) merged.paramIn = paramIn;
  return JSON.stringify(merged, null, 2);
}

// Save-time companion to `previewJson`: when an admin saves an endpoint
// while the editor is still in "View JSON" mode, the textarea — not the
// row form — is the source of truth. We have to peel `paramIn` back out
// of the unified JSON document so the wire payload matches what the
// server expects (sibling `parameters` + `paramIn`, not a nested field).
//
// Behavioural contract pinned by tests in
// `client/src/pages/__tests__/integrations.helpers.test.ts`:
//   - Empty / whitespace-only input falls back to the minimal
//     `{ type: "object", properties: {} }` schema with no `paramIn`,
//     mirroring `buildParamsFromRows([])`.
//   - Valid JSON containing a `paramIn` field is split: `paramIn` ends
//     up at the top level of the returned object, every other key stays
//     under `parameters`. The original parsed object is not mutated.
//   - Invalid JSON throws a labelled `Error` so the save mutation can
//     surface a clear per-endpoint message and abort instead of sending
//     garbage to the server. The label uses `endpointName` if non-blank
//     and falls back to `(unnamed)` to match the inline message that
//     used to live in `integrations.tsx`.
//
// `paramIn` is intentionally returned as `unknown` (effectively `any`)
// because power users can hand-author whatever they want here; the
// server is the authority on validation. We deliberately do not coerce
// or filter it — that would silently drop placement overrides the admin
// just typed, which is exactly the regression this helper exists to
// prevent.
export function buildParamsFromJsonMode(
  rawJson: string,
  endpointName: string,
): { parameters: any; paramIn: any } {
  const trimmed = rawJson.trim();
  if (!trimmed) {
    return { parameters: { type: "object", properties: {} }, paramIn: undefined };
  }
  let parsed: any;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    throw new Error(
      `Endpoint "${endpointName || "(unnamed)"}" has invalid JSON in parameters`,
    );
  }
  // Tolerate `null` / non-object payloads the same way the original
  // inline code did (the spread of `null ?? {}` yields `{}`).
  const { paramIn: extracted, ...rest } = (parsed && typeof parsed === "object" ? parsed : {}) as Record<string, unknown>;
  return { parameters: rest, paramIn: extracted };
}
